/**
 * Grabar aqui mismo la muestra de voz para clonar.
 *
 * El servidor no tiene microfono (peticion de Ricardo, 05/08/2026: "necesito
 * poder ejecutar eso en otro ordenador"), asi que la muestra se graba en el
 * navegador de cualquier equipo con micro —o del movil— y sube al backend, que
 * la valida y la deja donde el motor de voz clonada la busca.
 *
 * Reutiliza la captura de `lib/audio.ts`: el mismo PCM 16 kHz que ya se manda
 * al reconocimiento de voz. Aqui solo se acumula y se envuelve en un WAV.
 */
import { useEffect, useRef, useState } from 'react';
import { Boton } from '@/components/ui/button';
import { api } from '@/lib/api';
import * as audio from '@/lib/audio';
import { duracionDeTrozos, montarWav } from '@/lib/grabacion';
import { cn } from '@/lib/utils';

/** Lo que hay que leer: cifras y nombres del dominio, que es lo que dira. */
const TEXTO = `Buenos dias, soy Androcles. La posicion consolidada del grupo esta en un millon ochocientos mil euros, y ninguna cuenta esta por debajo de su umbral de seguridad. Ceslan ETT concentra la mayor parte, con un millon cuatrocientos dieciseis mil. El resto se reparte entre Formacion, Solucat, Heari y Eari Auditores.

Esta semana no hay vencimientos previstos. Le recuerdo que la cuenta de credito del Santander esta dispuesta en cincuenta y tres mil euros de los ciento cincuenta mil de limite, y que el parte de tesoreria de julio ya esta archivado.

Si necesita el detalle cuenta a cuenta, se lo leo cuando quiera. ¿Quiere que le mire algo mas en concreto, o lo dejamos aqui por hoy?`;

/** Minimo que acepta el backend. Se avisa antes de dejar subir. */
const MINIMO_SEGUNDOS = 20;

/** Objetivo comodo: con esto sobra para clonar. */
const OBJETIVO_SEGUNDOS = 75;

interface Props {
  className?: string;
}

export function GrabarMuestraVoz({ className }: Props) {
  const [abierto, setAbierto] = useState(false);
  const [grabando, setGrabando] = useState(false);
  const [segundos, setSegundos] = useState(0);
  const [nivel, setNivel] = useState(0);
  const [estado, setEstado] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const trozos = useRef<ArrayBuffer[]>([]);

  useEffect(() => {
    return () => {
      // Al desmontar (cambio de pestana, cierre): el microfono no se queda
      // abierto por descuido.
      audio.detenerCaptura();
    };
  }, []);

  const empezar = async () => {
    setError(null);
    setEstado(null);
    trozos.current = [];
    setSegundos(0);

    try {
      await audio.iniciarCaptura(
        (pcm) => {
          trozos.current.push(pcm);
          setSegundos(duracionDeTrozos(trozos.current));
        },
        // Silencio: aqui NO cierra nada. El jefe hace pausas al leer y el
        // turno no se corta hasta que pulsa "Ya está".
        () => undefined,
        {
          alNivel: setNivel,
          alFallo: (fallo) => {
            setError(`${fallo.titulo} ${fallo.consejo}`);
            setGrabando(false);
          },
        },
      );
      setGrabando(true);
    } catch (fallo) {
      const amable = fallo instanceof audio.ErrorAudio ? fallo : null;
      setError(
        amable ? `${amable.titulo} ${amable.consejo}` : 'No he podido encender el micrófono.',
      );
    }
  };

  const terminar = async () => {
    audio.detenerCaptura();
    setGrabando(false);
    setNivel(0);

    const duracion = duracionDeTrozos(trozos.current);
    if (duracion < MINIMO_SEGUNDOS) {
      setError(
        `Solo se han grabado ${Math.round(duracion)} segundos y hacen falta ` +
          `al menos ${MINIMO_SEGUNDOS}. Vuelve a intentarlo.`,
      );
      return;
    }

    setEstado('Enviando…');
    try {
      const respuesta = await api.subirMuestraVoz(montarWav(trozos.current));
      setEstado(`Guardada (${Math.round(duracion)} s). Ya se puede clonar la voz.`);
      trozos.current = [];
      if (!respuesta.guardada) setError('El servidor no la ha dado por buena.');
    } catch (fallo) {
      setEstado(null);
      setError(fallo instanceof Error ? fallo.message : 'No se ha podido enviar.');
    }
  };

  if (!abierto) {
    return (
      <Boton variante="contorno" className={cn('w-full', className)} onClick={() => setAbierto(true)}>
        Grabar mi voz
      </Boton>
    );
  }

  const progreso = Math.min(100, (segundos / OBJETIVO_SEGUNDOS) * 100);

  return (
    <section
      className={cn('flex flex-col gap-4 rounded-lg border-2 border-border bg-card p-5', className)}
      aria-label="Grabar la muestra de voz"
    >
      <header className="flex items-center justify-between gap-4">
        <h3 className="text-base font-bold">Muestra de voz</h3>
        {!grabando && (
          <button
            type="button"
            className="text-sm text-muted-foreground underline"
            onClick={() => {
              audio.detenerCaptura();
              setAbierto(false);
            }}
          >
            cerrar
          </button>
        )}
      </header>

      <p className="text-sm text-muted-foreground">
        Lee esto en tono normal de conversación, sin ruido alrededor. Con un minuto sobra.
      </p>

      <div className="max-h-56 overflow-y-auto rounded border border-border/70 bg-background p-4">
        <p className="whitespace-pre-wrap text-sm leading-relaxed">{TEXTO}</p>
      </div>

      {grabando && (
        <div className="flex flex-col gap-2">
          <div className="h-3 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-[hsl(var(--cobro))] transition-[width]"
              style={{ width: `${progreso}%` }}
            />
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className={nivel > 0.02 ? 'text-[hsl(var(--cobro))]' : 'text-destructive'}>
              {nivel > 0.02 ? 'Te estoy oyendo' : 'No te oigo: acercate al micrófono'}
            </span>
            <span className="tabular-nums text-muted-foreground">
              {Math.round(segundos)} s / {OBJETIVO_SEGUNDOS} s
            </span>
          </div>
        </div>
      )}

      {error && (
        <p className="rounded border-2 border-destructive bg-destructive/10 p-3 text-sm" role="alert">
          {error}
        </p>
      )}
      {estado && (
        <p className="rounded border-2 border-[hsl(var(--cobro))] p-3 text-sm">{estado}</p>
      )}

      <div className="flex gap-3">
        {!grabando ? (
          <Boton className="flex-1" onClick={() => void empezar()}>
            Empezar a grabar
          </Boton>
        ) : (
          <Boton variante="destructivo" className="flex-1" onClick={() => void terminar()}>
            Ya está, guardar
          </Boton>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        La grabación se queda en el servidor de Ceslan. No se sube a ningún otro sitio.
      </p>
    </section>
  );
}
